import { useEffect, useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import GlobalSearch from '../GlobalSearch'
import { getSocket } from '../../services/socket'
import { notificarMensaje, pedirPermiso, precargarSonidos } from '../../services/notifications'
import { suscribirPush } from '../../services/push'
import { suscribirFcm } from '../../services/fcm'
import MixLogo from '../MixLogo'

function useDarkMode() {
  const [dark, setDark] = useState(() => localStorage.getItem('theme') === 'dark')
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light')
    localStorage.setItem('theme', dark ? 'dark' : 'light')
  }, [dark])
  return [dark, setDark]
}

function useSidebar() {
  const [open, setOpen] = useState(() => localStorage.getItem('sidebar_open') !== 'false')
  useEffect(() => { localStorage.setItem('sidebar_open', open) }, [open])
  return [open, setOpen]
}

// El orden sigue el día a día: primero donde se atiende y se vende, después
// marketing, luego la administración, y al final lo que se consulta de vez en
// cuando (métricas y reportes).
const SECTIONS = [
  {
    label: 'Operación',
    items: [
      { ruta: '/inbox',     icono: '💬', label: 'Inbox',     roles: ['admin','supervisor','agente'] },
      { ruta: '/negocios',  icono: '🏢', label: 'Negocios',  roles: ['admin'] },
      { ruta: '/sesiones',  icono: '📱', label: 'Números',   roles: ['admin'] },
      { ruta: '/contactos', icono: '👥', label: 'Contactos', roles: ['admin','supervisor','agente'] },
      { ruta: '/pedidos',   icono: '🛒', label: 'Pedidos',   roles: ['admin','supervisor'] },
      { ruta: '/proyectos', icono: '📐', label: 'Proyectos', roles: ['admin','supervisor'] },
      { ruta: '/pipeline',  icono: '📊', label: 'Pipeline',  roles: ['admin','supervisor','agente'] },
    ]
  },
  {
    label: 'Marketing',
    items: [
      { ruta: '/campanas',   icono: '📢', label: 'Campañas',   roles: ['admin','supervisor'] },
      { ruta: '/plantillas', icono: '📋', label: 'Plantillas', roles: ['admin','supervisor','agente'] },
      { ruta: '/respuestas', icono: '⚡', label: 'Respuestas', roles: ['admin','supervisor','agente'] },
    ]
  },
  {
    label: 'Administración',
    items: [
      // Las integraciones (AlphaStock, APIs, key de IA) NO van aquí: viven
      // dentro de cada número, en Números → editar → Integraciones, que es
      // donde realmente se usan. /integraciones es solo un redirect heredado.
      { ruta: '/equipo', icono: '🧑‍💼', label: 'Equipo', roles: ['admin'] },
      { ruta: '/config', icono: '⚙️',  label: 'Config', roles: ['admin'] },
    ]
  },
  {
    label: 'Análisis',
    items: [
      { ruta: '/metricas-bot', icono: '🤖', label: 'Métricas IA', roles: ['admin','supervisor'] },
      { ruta: '/reportes',     icono: '📈', label: 'Reportes',    roles: ['admin','supervisor'] },
    ]
  },
]

function LogoutModal({ onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal animate-scale-in" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header" style={{ borderBottom: '1px solid var(--border)', paddingBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: 'rgba(239,68,68,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20
            }}>🚪</div>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Cerrar sesión</h3>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>Se cerrará tu sesión actual</p>
            </div>
          </div>
        </div>
        <div className="modal-body" style={{ padding: '20px 24px' }}>
          <p style={{ fontSize: 14, color: 'var(--text-soft)', lineHeight: 1.6 }}>
            ¿Estás seguro que deseas cerrar sesión? Tendrás que volver a iniciar sesión para acceder a MixCRM.
          </p>
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '0 24px 20px' }}>
          <button className="btn" onClick={onCancel} style={{ minWidth: 100 }}>
            Cancelar
          </button>
          <button className="btn btn-danger" onClick={onConfirm} style={{ minWidth: 120 }}>
            Sí, cerrar sesión
          </button>
        </div>
      </div>
    </div>
  )
}

export default function AppLayout() {
  const { usuario, empresa, plan, logout } = useAuth()
  const navigate = useNavigate()
  const [dark, setDark] = useDarkMode()
  const [sidebarOpen, setSidebarOpen] = useSidebar()
  const [showLogout, setShowLogout] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)

  useEffect(() => {
    pedirPermiso().then((permiso) => { if (permiso === 'granted') suscribirPush() })
    suscribirFcm()
    precargarSonidos()
    const sock = getSocket()
    if (!sock) return
    const onMsg = ({ chatId, contacto, mensaje, archivado, silenciado }) => {
      if (!contacto) return
      // Archivados y papelera no generan sonido ni notificación
      if (silenciado ?? archivado) return
      const chatAbierto = localStorage.getItem('inbox_chat_activo')
      notificarMensaje({
        nombreContacto: contacto?.nombre || contacto?.telefono || 'Nuevo mensaje',
        texto: mensaje?.texto,
        chatId,
        chatEstaAbierto: chatAbierto === chatId,
        onClick: () => navigate(`/inbox?chat=${chatId}`),
      })
    }
    sock.on('mensaje-nuevo', onMsg)
    return () => sock.off('mensaje-nuevo', onMsg)
  }, [navigate])

  const visibleSections = SECTIONS.map(sec => ({
    ...sec,
    items: sec.items.filter(i => i.roles.includes(usuario.rol))
  })).filter(sec => sec.items.length > 0)

  // Mobile: ajustar la altura de la app al viewport visible (teclado) y
  // ocultar el bottom nav cuando el teclado virtual está abierto.
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const initial = vv.height
    const onResize = () => {
      document.documentElement.style.setProperty('--app-height', `${vv.height}px`)
      document.body.classList.toggle('keyboard-open', vv.height < initial * 0.75)
    }
    onResize() // valor inicial
    vv.addEventListener('resize', onResize)
    return () => {
      vv.removeEventListener('resize', onResize)
      document.documentElement.style.removeProperty('--app-height')
    }
  }, [])

  return (
    <div className="app-shell">

      {/* ── Sidebar ── */}
      <nav className={`sidenav${sidebarOpen ? ' open' : ''}`}>

        {/* Header */}
        <div className="sidenav-head">
          <div className="sidenav-brand">
            <div className="sidenav-brand-logo" style={{ borderRadius: 8, overflow: 'hidden', lineHeight: 0 }}>
              <MixLogo size={28} />
            </div>
            <span className="sidenav-brand-name">MixCRM</span>
          </div>
          <button className="sidenav-toggle" onClick={() => setSidebarOpen(o => !o)} title={sidebarOpen ? 'Colapsar' : 'Expandir'}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d={sidebarOpen ? 'M9 2L4 7l5 5' : 'M5 2l5 5-5 5'}
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Nav sections */}
        <div className="sidenav-scroll">
          {visibleSections.map(sec => (
            <div key={sec.label}>
              <div className="sidenav-section">{sec.label}</div>
              {sec.items.map(item => (
                <NavLink
                  key={item.ruta}
                  to={item.ruta}
                  className={({ isActive }) => `sidenav-item${isActive ? ' active' : ''}`}
                >
                  <span className="sidenav-icon">{item.icono}</span>
                  <span className="sidenav-label">{item.label}</span>
                  <span className="tooltip">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="sidenav-footer">
          <button
            className="sidenav-item"
            onClick={() => setDark(d => !d)}
            title={dark ? 'Modo claro' : 'Modo oscuro'}
          >
            <span className="sidenav-icon">{dark ? '☀️' : '🌙'}</span>
            <span className="sidenav-label">{dark ? 'Modo claro' : 'Modo oscuro'}</span>
            <span className="tooltip">{dark ? 'Modo claro' : 'Modo oscuro'}</span>
          </button>
          <button
            className="sidenav-item"
            onClick={() => setShowLogout(true)}
            title="Cerrar sesión"
          >
            <span className="sidenav-icon">🚪</span>
            <span className="sidenav-label">Cerrar sesión</span>
            <span className="tooltip">Cerrar sesión</span>
          </button>
        </div>
      </nav>

      {/* ── Modal logout ── */}
      {showLogout && (
        <LogoutModal
          onConfirm={logout}
          onCancel={() => setShowLogout(false)}
        />
      )}

      {/* ── Main content ── */}
      <div className="main-content">
        <div className="topbar">
          <div className="flex items-center gap-3">
            <span className="topbar-title">{empresa?.nombre}</span>
            {plan && <span className="badge badge-primary">{plan.nombre}</span>}
            {empresa?.estado === 'trial' && <span className="badge badge-warning">Trial</span>}
          </div>
          <div className="topbar-actions">
            <div className="avatar avatar-sm" title={usuario.nombre}>
              {(usuario.nombre || '?')[0].toUpperCase()}
            </div>
            <span className="text-sm text-soft">{usuario.nombre}</span>
            <span className="badge">{usuario.rol}</span>
          </div>
        </div>
        <GlobalSearch />
        <Outlet />
      </div>

      {/* ── Bottom navigation — solo mobile ── */}
      <nav className="mobile-bottom-nav">
        <NavLink to="/inbox" className={({ isActive }) => isActive ? 'active' : ''}>
          <span className="nav-icon">💬</span>
          <span>Inbox</span>
        </NavLink>
        <NavLink to="/negocios" className={({ isActive }) => isActive ? 'active' : ''}>
          <span className="nav-icon">🏢</span>
          <span>Negocios</span>
        </NavLink>
        <NavLink to="/sesiones" className={({ isActive }) => isActive ? 'active' : ''}>
          <span className="nav-icon">📱</span>
          <span>Números</span>
        </NavLink>
        {/* Contactos se usa mucho más a diario que Config, que vive en "Más" */}
        <NavLink to="/contactos" className={({ isActive }) => isActive ? 'active' : ''}>
          <span className="nav-icon">👥</span>
          <span>Contactos</span>
        </NavLink>
        <button className={`mobile-more-trigger${moreOpen ? ' active' : ''}`} onClick={() => setMoreOpen(o => !o)}>
          <span className="nav-icon">
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <circle cx="6.5" cy="6.5" r="2.5" fill="currentColor"/>
              <circle cx="15.5" cy="6.5" r="2.5" fill="currentColor"/>
              <circle cx="6.5" cy="15.5" r="2.5" fill="currentColor"/>
              <circle cx="15.5" cy="15.5" r="2.5" fill="currentColor"/>
            </svg>
          </span>
          <span>Más</span>
        </button>
      </nav>

      {/* ── More sheet — solo mobile ── */}
      {moreOpen && (
        <>
          <div className="mobile-more-overlay" onClick={() => setMoreOpen(false)} />
          <div className="mobile-more-sheet">
            <div className="mobile-more-handle" />

            {/* Usuario */}
            <div className="mobile-more-user">
              <div className="mobile-more-avatar">
                {(usuario.nombre || '?')[0].toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)', lineHeight: 1.2 }}>{usuario.nombre}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{usuario.rol} · {empresa?.nombre}</div>
              </div>
              {plan && <span className="badge badge-primary" style={{ flexShrink: 0 }}>{plan.nombre}</span>}
            </div>

            {/* Todas las secciones */}
            {visibleSections.map(sec => (
              <div key={sec.label} className="mobile-more-section">
                <div className="mobile-more-section-label">{sec.label}</div>
                <div className="mobile-more-grid">
                  {sec.items.map(item => (
                    <NavLink
                      key={item.ruta}
                      to={item.ruta}
                      onClick={() => setMoreOpen(false)}
                      className={({ isActive }) => `mobile-more-item${isActive ? ' active' : ''}`}
                    >
                      <div className="mobile-more-item-icon">{item.icono}</div>
                      <div className="mobile-more-item-label">{item.label}</div>
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}

            {/* Acciones */}
            <div className="mobile-more-footer">
              <button className="mobile-more-footer-btn" onClick={() => setDark(d => !d)}>
                <span style={{ fontSize: 18 }}>{dark ? '☀️' : '🌙'}</span>
                <span>{dark ? 'Modo claro' : 'Modo oscuro'}</span>
              </button>
              <button className="mobile-more-footer-btn danger" onClick={() => { setMoreOpen(false); setShowLogout(true) }}>
                <span style={{ fontSize: 18 }}>🚪</span>
                <span>Cerrar sesión</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
